/**
 * watchlistStore — Multi-Watchlist Phase 2 external store (Jul 6, 2026).
 *
 * Mirrors the `portfolioStore` pattern but for the Phase 2 multi-list
 * feature. Every surface that renders a ★ star icon or reads "which
 * tickers am I watching?" ultimately funnels through this store's
 * active watchlist id so switching lists atomically re-paints the
 * entire app.
 *
 * Events emitted on the global window:
 *   - `watchlist-changed`     — fired on activate / rename / create /
 *                                delete. Any consumer that renders
 *                                stars or watched-ticker counts listens
 *                                to this and re-fetches.
 *   - `portfolio-changed`     — reused for backward compat with
 *                                existing Dashboard subscribers that
 *                                already listen for both events.
 *
 * Contract with backend:
 *   GET    /api/watchlists                  → hydrate()
 *   POST   /api/watchlists                  → create()
 *   PATCH  /api/watchlists/{id}             → rename()
 *   DELETE /api/watchlists/{id}             → deleteList()
 *   POST   /api/watchlists/{id}/activate    → setActive()
 *
 * State shape:
 *   {
 *     loaded: boolean,
 *     lists: [{ id, name, is_main, is_active, entry_count,
 *               pending_deletion_at, created_at, updated_at }],
 *     activeId: string | null,
 *     isPlus: boolean,           // derived from /api/me/limits
 *     caps: {
 *       custom_watchlist_limit: number,  // 0 for free, 4 for Plus
 *       watchlist_limit: number,         // 10 / 50
 *     },
 *   }
 */

import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let state = {
  loaded: false,
  lists: [],
  activeId: null,
  isPlus: false,
  caps: { custom_watchlist_limit: 0, watchlist_limit: 10 },
};

const listeners = new Set();

function emit() {
  for (const l of listeners) l();
}

function fireGlobal() {
  try {
    window.dispatchEvent(new Event('watchlist-changed'));
    window.dispatchEvent(new Event('portfolio-changed'));
  } catch (_e) { /* SSR */ }
}

export function subscribe(l) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function getSnapshot() {
  return state;
}

export async function hydrate() {
  // Load both endpoints in parallel — /me/limits gives us Plus status +
  // caps, /watchlists gives us the list of lists.
  try {
    const [limitsRes, listsRes] = await Promise.all([
      axios.get(`${API}/me/limits`, { withCredentials: true }),
      axios.get(`${API}/watchlists`, { withCredentials: true }),
    ]);
    const limits = limitsRes.data || {};
    const lists = (listsRes.data && listsRes.data.watchlists) || [];
    state = {
      loaded: true,
      lists,
      activeId: (lists.find((w) => w.is_active) || {}).id || null,
      // Plus is anyone with a >0 custom watchlist cap (admins + paying
      // Plus users). Matches how the backend gates create in
      // WatchlistSelector's "+ New Watchlist" affordance.
      isPlus: (limits.custom_watchlist_limit || 0) > 0,
      caps: {
        custom_watchlist_limit: limits.custom_watchlist_limit || 0,
        watchlist_limit: limits.watchlist_limit || 10,
      },
    };
    emit();
    return state;
  } catch (_e) {
    // Silent — anonymous / signed-out visitors just see the free tier
    // fallback via the selector's `loaded && lists.length === 0` gate.
    state = { ...state, loaded: true };
    emit();
    return state;
  }
}

export async function setActive(watchlistId) {
  if (!watchlistId || watchlistId === state.activeId) return;
  await axios.post(`${API}/watchlists/${watchlistId}/activate`, {}, { withCredentials: true });
  // Optimistically flip the active flag locally so the dropdown label
  // updates instantly. hydrate() then reconciles with server truth.
  state = {
    ...state,
    lists: state.lists.map((w) => ({ ...w, is_active: w.id === watchlistId })),
    activeId: watchlistId,
  };
  emit();
  fireGlobal();
  // Full re-hydrate so entry_count on every list stays accurate after
  // switching (the active list's entries may have changed since last
  // fetch if another tab modified them).
  await hydrate();
}

export async function createWatchlist(name) {
  const res = await axios.post(`${API}/watchlists`, { name }, { withCredentials: true });
  const created = res.data;
  state = { ...state, lists: [...state.lists, created] };
  emit();
  fireGlobal();
  return created;
}

export async function renameWatchlist(watchlistId, name) {
  await axios.patch(`${API}/watchlists/${watchlistId}`, { name }, { withCredentials: true });
  state = {
    ...state,
    lists: state.lists.map((w) => (w.id === watchlistId ? { ...w, name } : w)),
  };
  emit();
  fireGlobal();
}

export async function deleteWatchlist(watchlistId) {
  const res = await axios.delete(`${API}/watchlists/${watchlistId}`, { withCredentials: true });
  const nextLists = state.lists.filter((w) => w.id !== watchlistId);
  // If the deleted list was active, Main gets auto-reactivated
  // server-side; re-hydrate to pick that up.
  state = { ...state, lists: nextLists };
  emit();
  fireGlobal();
  await hydrate();
  return res.data;
}

// Feb 2026 (Round P111h) — External-mutation subscription.
// Star toggles on StockModal / stock rows fire `watchlist-changed`
// via `WatchlistStarButton.jsx`, and the "Clear Watchlist" bulk
// button fires the same event. Without this listener, the store's
// cached `entry_count` on each list stays stale until the user
// navigates away and back (which forced a fresh mount + hydrate).
// Result: the selector pill said "1/50" long after the list was
// emptied. Debounced so an in-flight hydrate doesn't cascade.
let _hydrateInFlight = false;
async function _debouncedHydrate() {
  if (_hydrateInFlight) return;
  _hydrateInFlight = true;
  try { await hydrate(); } finally { _hydrateInFlight = false; }
}
if (typeof window !== 'undefined') {
  window.addEventListener('watchlist-changed', _debouncedHydrate);
  // `portfolio-changed` is fired by the same star toggle and by the
  // legacy /api/portfolio DELETE path (Clear Watchlist bulk button).
  window.addEventListener('portfolio-changed', _debouncedHydrate);
}

// Feb 2026 (Round P111i) — Star Sync Across Tabs.
// If the user has DiviDrip open in two tabs and adds/removes a star
// in tab A, tab B's selector should update its count too. Uses
// `BroadcastChannel` (built in to every modern browser) to relay
// `watchlist-changed` / `portfolio-changed` between same-origin
// tabs. `_broadcastEcho` guards against the infinite-loop where a
// tab receives an echo and re-broadcasts it.
let _broadcastEcho = false;
if (typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined') {
  try {
    const _ch = new BroadcastChannel('divdrip-watchlist');
    const _relay = (name) => (evt) => {
      if (_broadcastEcho) return;  // this event was itself an echo
      // Don't leak sensitive detail across tabs — the event name is
      // the whole payload the receiver needs.
      try { _ch.postMessage({ name, ticker: evt?.detail?.ticker || null, ts: Date.now() }); }
      catch (_e) { /* ignore */ }
    };
    window.addEventListener('watchlist-changed', _relay('watchlist-changed'));
    window.addEventListener('portfolio-changed', _relay('portfolio-changed'));
    _ch.onmessage = (msg) => {
      const name = msg?.data?.name;
      if (name !== 'watchlist-changed' && name !== 'portfolio-changed') return;
      _broadcastEcho = true;
      try {
        // Re-fire locally so the store re-hydrates AND anything else
        // listening in this tab (AppHeader star count, WatchlistPage
        // list, etc.) reacts as if a local mutation happened.
        window.dispatchEvent(new CustomEvent(name, {
          detail: { ticker: msg.data.ticker || null, fromBroadcast: true },
        }));
      } finally {
        // Give the sync listeners a tick to finish, then clear the
        // guard so the next legitimate local event can broadcast.
        setTimeout(() => { _broadcastEcho = false; }, 0);
      }
    };
  } catch (_e) {
    // BroadcastChannel throws in some sandboxed iframes / very old
    // browsers. Falling back to single-tab behaviour is fine.
  }
}
